import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { GridFilterModel, GridSortModel } from '@mui/x-data-grid';
import {
  DIAM_API_PATH,
  DIAM_TEAM_ID,
  DiamAuditLogFilter,
  DiamPageSortField,
  getAuditLogsPage,
} from '../../diamApi';
import { useApiEssentials } from '../../../../shared/hooks/useApiEssentials';
import {
  buildAuditLogsPageFilter,
  toDiamSortFields,
} from '../../diamGridUtils';

export const AUDIT_LOGS_FILTER: DiamAuditLogFilter = {
  teamId: DIAM_TEAM_ID,
  teamScope: 'CHILD',
};

export interface UseAuditLogsPageParams {
  page: number;
  pageSize: number;
  sortModel: GridSortModel;
  filterModel: GridFilterModel;
}

const DEFAULT_SORT: DiamPageSortField[] = [
  { field: 'createdDate', direction: 'DESC' },
];

export function useAuditLogs({
  page,
  pageSize,
  sortModel,
  filterModel,
}: UseAuditLogsPageParams) {
  const { getIdToken, apiUrl, isSignedIn } = useApiEssentials(DIAM_API_PATH);
  const filter = buildAuditLogsPageFilter(AUDIT_LOGS_FILTER, filterModel);
  const sort = toDiamSortFields(sortModel, DEFAULT_SORT);

  return useQuery({
    queryKey: [
      'diam',
      'audit-logs',
      'page',
      page,
      pageSize,
      sort,
      filter,
    ] as const,
    queryFn: async () => {
      const idToken = await getIdToken();
      return getAuditLogsPage(apiUrl, idToken, {
        page,
        limit: pageSize,
        sort,
        filter,
      });
    },
    enabled: isSignedIn,
    placeholderData: keepPreviousData,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
